import { SentimentDissatisfied, Search } from "@mui/icons-material";
import {
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useState } from "react";
import { config } from "../App";
import Footer from "./Footer";
import Header from "./Header";
import ProductCard from "./ProductCard";
import { generateCartItemsFrom } from "./Cart";
import Cart from "./Cart";
import "./Products.css";



const Products = () => {
  const [items, setItems] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const [debounceTimeout, setDebounceTimeout] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);

  const token = localStorage.getItem("token");

  const performAPICall = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${config.endpoint}/products`);

      setLoading(false);
      setProducts(response.data);
      setFilteredProducts(response.data);
      return response.data;
    } catch (e) {
      setLoading(false);

      if (e.response && e.response.status === 500) {
        enqueueSnackbar(e.response.data.message, { variant: "error" });
        return null;
      } else {
        enqueueSnackbar(
          "Could not fetch products. Check that the backend is running, reachable, and returning valid JSON.",
          {
            variant: "error",
          }
        );
      }
    }
  }, [enqueueSnackbar]);

  const performSearch = useCallback(async (text) => {
    try {
      const response = await axios.get(
        `${config.endpoint}/products/search?value=${text}`
      );
      setFilteredProducts(response.data);
      return response.data;
    } catch (e) {
      if (e.response) {
        if (e.response.status === 404) {
          setFilteredProducts([]);
        }
        if (e.response.status === 500) {
          enqueueSnackbar(e.response.data.message, { variant: "error" });
          setFilteredProducts(products);
        }
      } else {
        enqueueSnackbar(
          "Could not fetch products. Check that the backend is running, reachable, and returning valid JSON.",
          {
            variant: "error",
          }
        );
      }
    }
  }, [enqueueSnackbar, products]);

  const debounceSearch = useCallback(
    (event, debounceTimeout) => {
      const value = event.target.value;

      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      const timeout = setTimeout(() => {
        performSearch(value);
      }, 500);

      setDebounceTimeout(timeout);
    },
    [performSearch]
  );

  const fetchCart = useCallback(async (token) => {
    if (!token) return;

    try {
      const response = await axios.get(`${config.endpoint}/cart`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (e) {
      if (e.response && e.response.status === 400) {
        enqueueSnackbar(e.response.data.message, { variant: "error" });
      } else {
        enqueueSnackbar(
          "Could not fetch cart. Check that the backend is running, reachable, and returning valid JSON.",
          {
            variant: "error",
          }
        );
      }

      return null;
    }
  }, [enqueueSnackbar]);

  const updateCartItems = useCallback(
    (cartData, products) => {
      const cartItems = generateCartItemsFrom(cartData, products);
      setItems(cartItems);
    },
    []
  );

  const isItemInCart = useCallback(
    (items, productId) => {
      if (items) {
        return items.findIndex((item) => item.productId === productId) !== -1;
      }
      return false;
    },
    []
  );

  const addToCart = useCallback(
    async (
      token,
      items,
      products,
      productId,
      qty,
      options = { preventDuplicate: false }
    ) => {
      if (!token) {
        enqueueSnackbar("Login to add an Item to Cart", { variant: "warning" });
        return;
      }

      if (options.preventDuplicate && isItemInCart(items, productId)) {
        enqueueSnackbar(
          "Item already in Cart. Use the sidebar to update the quantity or remove items.",
          {
            variant: "error",
          }
        );
        return;
      }

      try {
        const response = await axios.post(
          `${config.endpoint}/cart`,
          { productId, qty },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        updateCartItems(response.data, products);
      } catch (e) {
        if (e.response) {
          enqueueSnackbar(e.response.data.message, { variant: "error" });
        } else {
          enqueueSnackbar(
            "Could not add item to cart. Check that the backend is running, reachable, and returning valid JSON.",
            {
              variant: "error",
            }
          );
        }
      }
    },
    [enqueueSnackbar, isItemInCart, updateCartItems]
  );

  useEffect(() => {
    const onLoadHandler = async () => {
      const productsData = await performAPICall();
      const cartData = await fetchCart(token);
      const cartDetails = await generateCartItemsFrom(cartData, productsData);
      setItems(cartDetails);
    };
    onLoadHandler();
  }, [token, fetchCart, performAPICall]);

  return (
    <div>
      <Header>
        <TextField
          className="search-desktop"
          size="small"
          InputProps={{
            className: "search",
            endAdornment: (
              <InputAdornment position="end">
                <Search color="primary" />
              </InputAdornment>
            ),
          }}
          placeholder="Search for items/categories"
          name="search"
          onChange={(e) => debounceSearch(e, debounceTimeout)}
        />
      </Header>

      <Grid container>
        <Grid
          item
          xs={12}
          md={token && products.length ? 9 : 12}
          className="product-grid"
        >
          <Box className="hero">
            <p className="hero-heading">
              India’s <span className="hero-highlight">FASTEST DELIVERY</span>{" "}
              to your doorstep
            </p>
          </Box>
          {isLoading ? (
            <Box className="loading">
              <CircularProgress />
              <h4>Loading Products...</h4>
            </Box>
          ) : (
            <Grid container marginY="1rem" paddingX= "1rem" spacing={2}>
              {filteredProducts.length ? (
                filteredProducts.map((product) => (
                  <Grid item xs={6} md={3} key={product._id}>
                    <ProductCard
                      product={product}
                      handleAddToCart={async () => {
                        await addToCart(
                          token,
                          items,
                          products,
                          product._id,
                          1,
                          {
                            preventDuplicate: true,
                          }
                        );
                      }}
                    />
                  </Grid>
                ))
              ) : (
                <Box className="loading">
                  <SentimentDissatisfied color="action" />
                  <h4 style={{ color: "#636363" }}>No Products found</h4>
                </Box>
              )}
            </Grid>
          )}
        </Grid>

        {token ? (
          <Grid item xs={12} md={3} bgcolor="#E9F5E1">
            {/* Add your Cart component here */}
            <Cart
              hasCheckoutButton
              products={products}
              items={items}
              handleQuantity={addToCart}
            />
          </Grid>
        ) : null}
      </Grid>
      <Footer />
    </div>
  );
};

export default Products;
